import React, { useState, useEffect } from 'react';
import Link from '@mui/material/Link';
import {
  Box,
  Typography,
  CircularProgress,
  Fade,
  Card,
  CardHeader,
  CardActions,
  CardContent,
  Chip,
  Divider,
  Stack,
  TextField,
  InputAdornment,
  Tooltip,
} from '@mui/material';
import { IconButton, Collapse } from '@mui/material';
import EditTwoToneIcon from '@mui/icons-material/EditTwoTone';
import CancelIcon from '@mui/icons-material/Cancel';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';

import Button from '@mui/material/Button';
import Title from '../../components/Title';
import SearchBar from '../../components/SearchBar';
import EditModal from '../../components/EditModal';
import AddModal from '../../components/AddModal';
import UploadModal from '../../components/UploadModal';
import { httpRequest } from '../../api/helper';
import { useNotification } from '../../context/NotificationContext';
import { API_ERROR_MESSAGE } from '../../common/AppConstants';

const TITLE = 'Classroom Types';
const API_URL = 'campus/classroom-type/';
const UPLOAD_TEMPLATE = 'room_type_upload_template.xlsx';
const NO_DATA_AVAILABLE_STRING =
  'No data available, please start adding now !!!';

function preventDefault(event) {
  event.preventDefault();
}

export default function RoomTypes() {
  const { addNotification } = useNotification();
  const [isLoading, setIsLoading] = useState(false);
  const [columns, setColumns] = useState([]);
  const [rows, setRows] = useState([]);
  const [relatedData, setRelatedData] = useState({});
  const [selectedEditRow, setSelectedEditRow] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  // Filtered data based on search
  const filteredRows = rows.filter(row_item =>
    row_item.code?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleAddClick = () => {
    setIsAddModalOpen(true);
  };

  const handleAddModalClose = () => {
    setIsAddModalOpen(false);
  };

  const handleUploadClick = () => {
    setIsUploadModalOpen(true);
  };

  const handleUploadModalClose = () => {
    setIsUploadModalOpen(false);
  };

  const handleEditClick = (row) => {
    console.log('Edit row ', row);
    setSelectedEditRow(row);
  };

  useEffect(() => {
    if (selectedEditRow !== null && selectedEditRow !== undefined) {
      // Only open the modal after the row has been selected
      setIsEditModalOpen(true);
    }
  }, [selectedEditRow]); // Runs when selectedEditRow changes

  const handleEditModalClose = () => {
    setIsEditModalOpen(false);
    setSelectedEditRow(null);
  };

  const handleEditSave = async (updatedEditRow, onEditFailure) => {
    await httpRequest({
      method: 'PUT',
      url: API_URL,
      data: updatedEditRow,
      onSuccess: (data) => {
        setRows((prevRows) =>
          prevRows.map((row, index) =>
            row.id === selectedEditRow.id ? { ...row, ...data } : row,
          ),
        );
        handleEditModalClose();
        refreshData();  // Other Primary are reset at backend - Only 1 can be primary
      },
      onFailure: (data) => {
        onEditFailure(data);
      },
      addNotification,
    });
  };

  const handleDelete = async (deletedRow) => {
    await httpRequest({
      method: 'DELETE',
      url: API_URL,
      data: deletedRow,
      onSuccess: (data) => {
        setRows((prevRows) =>
          prevRows.filter((row) => row.id !== deletedRow.id),
        );
      },
      addNotification,
    });
  };

  const handleAddSave = async (addFormData, onAddSuccess, onAddFailure) => {
    await httpRequest({
      method: 'POST',
      url: API_URL,
      data: addFormData,
      onSuccess: (data) => {
        handleAddModalClose();
        setRows((prevRows) => [data.entry, ...prevRows]);
        handleAddClick();
        onAddSuccess();
        refreshData();  // Other Primary are reset at backend - Only 1 can be primary
      },
      onFailure: (data) => {
        onAddFailure(data);
      },
      addNotification,
    });
  };

  const handleUpload = async (file, onUploadSuccess, onUploadFailure) => {
    const formData = new FormData();
    formData.append('file', file);

    await httpRequest({
      method: 'POST',
      url: `${API_URL}upload/`,
      data: formData,
      config: {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      },
      onSuccess: (data) => {
        refreshData();
        onUploadSuccess(data);
      },
      onFailure: (data) => {
        onUploadFailure(data);
      },
      addNotification,
    });
  };

  const handleFilterApply = async () => {
    // ToDo: Filter table data
  };

  useEffect(() => {
    const fetchData = async () => {
      refreshData();
    };
    fetchData();
  }, []);

  const refreshData = async (event) => {
    setIsLoading(true);

    await httpRequest({
      method: 'GET',
      url: API_URL,
      onSuccess: (data) => {
        setColumns(data.columns);
        setRows(data.rows);
        setRelatedData(data.relatedData);
      },
      onFailure: (data) => {
        setColumns([]);
        setRows([]);
        setRelatedData({});
      },
      addNotification,
    });

    setIsLoading(false);
  };

  const RoomTypeCard = ({ room_type_row, room_type_dict }) => {
    return (
      <Card
        variant="outlined"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          borderRadius: 2,
          transition: 'box-shadow 0.3s',
          '&:hover': {
            boxShadow: 3
          }
        }
      }>
        <CardHeader
          title={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="h4">{room_type_dict['code']}</Typography>
              {/* More data chips if needed */}
              { room_type_dict['primary'] && (
                <Tooltip title='Primary Classroom for student section' arrow>
                  <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    sx={{
                      height: '20px', // Reduce button height
                      padding: 0,
                    }}
                  >
                    Primary
                  </Button>
                </Tooltip>
              )}
            </Box>
          }
          action={
            <IconButton onClick={() => handleEditClick(room_type_dict)} size="small">
              <EditTwoToneIcon fontSize="small" />
            </IconButton>
          }
          sx={{ pb: 2 }}
        />
      </Card>
    );
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: 'calc(100vh - 64px - 64px)', // viewport height - margings - app bar
      }}
    >
      {/* Title and Button (fixed) */}
      <Box
        sx={{
          flexShrink: 0, // Top level space distribution
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Title */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            mt: 1,
            mb: 3,
            gap: 2,
          }}
        >
          <Box sx={{ flex: 1 }}>
            <Title>{TITLE}</Title>
          </Box>
          {/* Search Bar */}
          <SearchBar searchTerm={searchTerm} setSearchTerm={setSearchTerm} />
          <Box sx={{ flex: 1, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
            {/* <Button
              variant="contained"
              startIcon={<CloudUploadIcon />}
              onClick={handleUploadClick}
              sx={{
                px: 4,
                display: { md: 'flex', xs: 'none' },
              }}
            >
              Upload
            </Button> */}
            <Button
              variant="contained"
              startIcon={<AddCircleIcon />}
              onClick={handleAddClick}
              sx={{
                px: 4,
              }}
            >
              Add
            </Button>
          </Box>
        </Box>
      </Box>

      {/* Scrollable table container */}
      <Box
        sx={{
          flexGrow: 1, // This makes the table container grow and take the available space
          overflowY: 'auto', // Only the table will scroll
          // mt: 2,
          minHeight: 200, // Ensures proper space for loader
          display: 'flex',
          flexDirection: 'column', // Aligns table to the top when shown
          alignItems: isLoading ? 'center' : 'stretch', // Center loader, but stretch table
          justifyContent: isLoading ? 'center' : 'flex-start', // Center loader, start table at top
        }}
      >
        {isLoading ? (
          <Fade in={isLoading} timeout={500}>
            <CircularProgress size={50} thickness={3} />
          </Fade>
        ) : (
          <Fade in={!isLoading} timeout={500}>
            {rows.length === 0 ? (
              // Display message when there are no rows
              <Box
                sx={{
                  textAlign: 'left',
                }}
              >
                <Typography variant="h5">{NO_DATA_AVAILABLE_STRING}</Typography>
              </Box>
            ) : (
              <Box
                sx={{
                  width: '100%',
                  py: 2,
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: '1fr',
                    md: 'repeat(2, 1fr)',
                    xl: 'repeat(3, 1fr)',
                  },
                  gap: 1.5, // This creates even spacing without edge padding
                }}
              >
                {filteredRows.map((item_dict, index) => (
                  <Box key={index}>
                    <RoomTypeCard
                      room_type_row={index}
                      room_type_dict={item_dict}
                    />
                  </Box>
                ))}
              </Box>
            )}
          </Fade>
        )}
      </Box>

      {/* Add Section Modal */}
      <Box>
        <AddModal
          open={isAddModalOpen}
          columns={columns}
          relatedData={relatedData}
          rows={rows}
          handleClose={handleAddModalClose}
          handleSave={handleAddSave}
          add_title="Add Classroom Type"
        />
      </Box>

      {/* Upload Modal */}
      <UploadModal
        open={isUploadModalOpen}
        uploadTemplate={UPLOAD_TEMPLATE}
        handleClose={handleUploadModalClose}
        handleUpload={handleUpload}
      />

      {/* Edit Section Modal */}
      <Box>
        <EditModal
          open={isEditModalOpen}
          columns={columns}
          rowData={selectedEditRow}
          relatedData={relatedData}
          handleClose={handleEditModalClose}
          handleSave={handleEditSave}
          handleDelete={handleDelete}
          edit_title="Edit Classroom Type"
        />
      </Box>
    </Box>
  );
}
